import { Icon } from '../../../components/icons';
import { FeaturePageLayout, type FeatureContent } from './FeaturePageLayout';
import { BotDetectionStory } from './stories/BotDetectionStory';

/**
 * Marketing page for SieveGuard's flagship product — Bot Anomaly Detection +
 * Email Verification, merged into a single always-coupled action. Every
 * /v1/emails call runs both layers and charges the combined credit weight.
 * There is no verify-only or detection-only mode; the pre-merge feature pages
 * for each half have been folded here (the /features/email-verification URL
 * redirects to this one for SEO continuity).
 *
 *   - No `kind` field → not toggleable (core, always available).
 *   - `creditFeature: ['detection', 'emailVerification']` → the wallet-mode
 *     pricing card sums both weights so the callout matches the coupled
 *     "signup screening" row in the per-action cost table on the right.
 *   - No `addonKind` → the legacy-mode pricing card renders nothing.
 */
const CONTENT: FeatureContent = {
  slug: 'bot-detection',
  // Scroll-driven "how it works" — replaces the static numbered steps with the real
  // canonicalize → verdict pipeline. See ./stories/BotDetectionStory.tsx.
  story: <BotDetectionStory />,
  title: 'Bot Anomaly Detection & Email Verification',
  tagline:
    'One call per signup. Cluster bot waves, score risk, and confirm the mailbox actually receives mail — always together, in the same request.',
  eyebrow: 'Core product · always on',
  description:
    "Every /v1/emails call runs the full stack: near-duplicate clustering + a dozen structural heuristics for anomaly detection, then an authoritative mailbox-level check (deliverable / invalid / disposable / spamtrap). One response carries the risk score, cluster id, action band, reasons, and verification status. Bot waves surface as clusters minutes after they start, dead mailboxes get caught before they bounce, and spamtraps get force-declined so they can't tank your sender reputation.",
  icon: <Icon.ShieldAlert width={48} height={48} />,
  accent: 'accent',
  heroBg: '/heroes/feat-bot-detection.webp',
  pillars: [
    {
      title: 'Cluster detection — find the wave, not the address',
      body: 'Near-duplicate clustering groups look-alike addresses as they arrive, even when each one looks plausible in isolation (e.g. jane.smith42@gmail.com, jane.smith43@gmail.com, …). The cluster id rides on every record so your downstream systems can act per-wave, not per-address.',
    },
    {
      title: 'Per-address risk score + reasons',
      body: '0–100 risk score with a structured `reasons` array so you can branch on specific signals (spamtrap-shaped, low engagement, high digit ratio, disposable domain, …). Score → action ladder is `allow / verify / review / decline`, configurable per workspace.',
    },
    {
      title: 'Authoritative mailbox verification — bundled, always',
      body: "Every screening also runs the mailbox-level check that answers `does this address actually receive mail`. Four-state result: `deliverable / invalid / disposable / spamtrap` (plus `unknown` for the rare grey-zone case). Spamtrap detection is called out separately from `deliverable` so your code can hard-block the addresses that would poison your sender reputation.",
    },
    {
      title: 'One credit weight, one API call',
      body: 'Because detection + verification are one action, there\'s nothing to configure per request. No `verify: true|false` decisions, no partial-response ambiguity, no separate quotas to track. You send an address, you get back the full risk + deliverability picture, you spend the combined credit weight from your wallet.',
    },
  ],
  steps: [
    {
      title: 'Pipe your traffic through /v1/emails',
      body: 'One JSON POST per address (or up to 25,000 per async job). The response carries score + cluster id + action band + reason set + mailbox verification status — ready to gate the signup, forward to your review queue, or ship straight to your database.',
    },
    {
      title: 'Choose your action band thresholds',
      body: 'Default ladder is allow (0–24) / verify (25–59) / review (60–84) / decline (85–100). Editable per workspace from Profile → Detection. Whatever crosses the review threshold goes into the manual review queue or auto-declines depending on your `reviewMode` setting.',
    },
    {
      title: 'React on the webhook',
      body: '`emails.scored` fires on every screening. `email.duplicate_detected` fires when a new address lands in an existing cluster. `email.reviewed` fires when the manual queue resolves a record. All HMAC-signed with retry + DLQ.',
    },
    {
      title: 'Iterate on the workspace dashboard',
      body: 'The console shows the live cluster fleet, top reasons across the last 7 days, mailbox-verification result mix (deliverable / invalid / disposable / spamtrap), and a per-list score histogram — so you can spot where bot waves are concentrating before they spill across the whole tenant.',
    },
  ],
  included: [
    {
      scope: 'trial',
      items: [
        'Free screening budget for the trial window',
        'Full cluster + risk score + reasons + verification API',
        'Manual review queue',
      ],
    },
    {
      scope: 'pro',
      items: [
        'Every Pro tier includes detection + verification in the bundled credit allowance',
        'Higher monthly throughput before credit-pack top-up needed',
        'Spamtrap detection included at no extra credit cost',
      ],
    },
    {
      scope: 'addon',
      items: [
        'Top up with a credit pack when a traffic spike outruns your monthly allowance',
        'Predictable pricing — the per-screening credit weight never changes mid-cycle',
        'Same low-latency API whether the credits come from your plan, wallet, or a pack',
      ],
    },
  ],
  faq: [
    {
      q: 'What signals does the risk score fire on?',
      a: 'A mix of structural, behavioural, and mailbox-authority signals: digit-to-letter ratio, stem density / repeated padding, near-duplicate clustering against the recent fleet, disposable-domain match, MX validity, suspected spamtrap shape, and the authoritative mailbox verification result. Each contributing signal lands in the `reasons` array so you can branch deterministically — no opaque ML scores.',
    },
    {
      q: 'Can I skip the mailbox verification and just get the anomaly score?',
      a: 'No — the two layers are one product. Every /v1/emails call runs the full stack (near-duplicate clustering + structural risk scoring + authoritative mailbox check) and charges the combined credit weight from your wallet. There is no verify=false / anomaly-only mode; every response carries the mailbox-verification fields alongside the score.',
    },
    {
      q: 'How fast is the API?',
      a: 'Single-address screening is sub-300ms p95 on cache hits, sub-800ms p95 on cache misses (which include the mailbox check). Bulk jobs of up to 25,000 addresses stream progress via webhooks; large lists complete in seconds-per-thousand.',
    },
    {
      q: 'How is it priced?',
      a: 'Each screening consumes the combined detection + verification credit weight from your monthly wallet — the exact per-action cost is shown in the per-action cost table on the pricing page (live from the platform credit table). The weight is set so a normal signup funnel sits comfortably inside an entry Pro tier; credit packs cover the overflow if traffic spikes.',
    },
    {
      q: 'What\'s the cache TTL?',
      a: '30 days for the mailbox-verification result. Repeat lookups of the same address within the TTL don\'t re-hit the verifier — the cached result flows through. The anomaly + clustering side always runs live, since the whole point is to catch waves as they arrive.',
    },
    {
      q: 'What if the mailbox verifier is unavailable?',
      a: 'The API degrades to detection-only with `verifierSkippedReason: \'provider_down\'` on the response, and only the detection portion of the credit weight is charged — you don\'t pay for what didn\'t run. Automation resumes as soon as the verifier is healthy again.',
    },
    {
      q: 'Do I need Domain Health on top?',
      a: 'Only if you care about the sender-side story. Detection + verification answers "is this address safe to accept". Domain Health answers "is this sender safe to trust" — SPF / DKIM / DMARC / MX posture on the sending domain. Different questions; pair them if your funnel has both sides (B2B outreach with reply tracking is the classic case).',
    },
  ],
  creditFeature: ['detection', 'emailVerification'],
  relatedFeatures: [
    {
      slug: 'domain-health',
      title: 'Domain Health',
      tagline: 'Score every sender domain — SPF / DKIM / DMARC posture on every record.',
    },
    {
      slug: 'inbox-placement',
      title: 'Inbox Placement',
      tagline: 'Confirm where your verified senders are actually landing.',
    },
    {
      slug: 'blacklist-checks',
      title: 'Blacklist Checks',
      tagline: '50+ industry blacklists checked on every domain you ingest.',
    },
  ],
};

export function BotDetectionFeaturePage() {
  return <FeaturePageLayout content={CONTENT} />;
}

// Default export required by React Router framework-mode route modules.
export default BotDetectionFeaturePage;
